/**
 * The Service Switch screen — where an administrator decides what this
 * installation is.
 *
 * The screen's job is to make the consequences visible before anything is
 * clicked, because a toggle whose effects are invisible is a toggle nobody is
 * willing to touch. So each card says:
 *
 * - **what the service brings** — the modules it adds, listed;
 * - **what it needs** — and, when a requirement is missing, that the toggle is
 *   unavailable and why;
 * - **what needs it** — so turning something off names what would break;
 * - **why it is locked**, in words, whenever it cannot be changed.
 *
 * Three states, not two. `core` is mandatory; a service can be *chosen* by an
 * administrator or *implied* by something else that needs it — and an implied
 * one comes back the moment its dependent is switched off. Without that
 * distinction, a toggle that will not turn off looks broken.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Info, Lock, ToggleLeft } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  ErrorState,
  LoadingState,
  PageHeader,
  Switch,
  Textarea,
  api,
  toast,
  useSession,
} from '@connect/web-shared';

interface ServiceModule {
  id: string;
  label: string;
  group: string | null;
  kinds: string[];
}

interface ServiceRow {
  id: string;
  label: string;
  description: string;
  order: number;
  mandatory: boolean;
  requires: string[];
  required_by: string[];
  modules: ServiceModule[];
  /** An administrator switched it on. */
  chosen: boolean;
  /** It is on — chosen, implied by a dependent, or mandatory. */
  enabled: boolean;
  /** Why it cannot be changed right now, or null. */
  locked_reason: string | null;
  missing_requirements: string[];
  blocking_dependents: string[];
}

export function ServicesPage() {
  const queryClient = useQueryClient();
  const refreshSession = useSession((state) => state.refreshSession);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const services = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ services: ServiceRow[] }>('/services'),
  });

  const setService = useMutation({
    mutationFn: (input: { id: string; enabled: boolean; note?: string }) =>
      api.put<{ service_id: string; enabled: boolean }>(`/services/${input.id}`, {
        enabled: input.enabled,
        note: input.note ?? null,
      }),
    onSuccess: async (result, input) => {
      await queryClient.invalidateQueries({ queryKey: ['services'] });
      // The session carries the enabled services and the navigation, so the
      // sidebar has to be re-read — otherwise the screen says Finance is on
      // and the menu still does not show it.
      await refreshSession();
      const row = services.data?.services.find((service) => service.id === input.id);
      toast.success(
        `${row?.label ?? result.service_id} ${result.enabled ? 'enabled' : 'disabled'}`,
        result.enabled
          ? 'Its screens are now available to anyone whose role includes them.'
          : 'Its screens are hidden and its API refuses calls. No data was deleted.',
      );
      setNoteFor(null);
      setNote('');
    },
    onError: (error) => {
      // The API refuses with the reason — which requirement is missing, which
      // dependent is in the way — so it is shown verbatim.
      toast.error(
        'Could not change that service',
        error instanceof ApiError ? error.message : undefined,
      );
    },
  });

  if (services.isLoading) return <LoadingState label="Reading the service catalog" />;
  if (services.isError) {
    return (
      <ErrorState
        message={services.error instanceof ApiError ? services.error.message : undefined}
        onRetry={() => void services.refetch()}
      />
    );
  }

  const rows = services.data?.services ?? [];
  const enabledCount = rows.filter((row) => row.enabled).length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Services"
        description="What this installation is made of. A service that is off has no screens and its API refuses calls."
        breadcrumbs={[{ label: 'Settings', to: '/settings' }, { label: 'Services' }]}
        actions={
          <Badge tone="accent">
            {enabledCount} of {rows.length} enabled
          </Badge>
        }
      />

      <Alert tone="info" title="Switching a service off hides it — it does not delete anything">
        Records already created stay in the database and come back unchanged if the service is
        enabled again. What changes is that its screens disappear and its API stops answering, for
        everybody including administrators.
      </Alert>

      <div className="grid gap-3 lg:grid-cols-2">
        {rows.map((row) => {
          const implied = row.enabled && !row.chosen && !row.mandatory;
          const locked = Boolean(row.locked_reason);
          const noteOpen = noteFor === row.id;

          return (
            <Card key={row.id} className={row.enabled ? 'border-accent/25' : undefined}>
              <CardBody className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-heading">{row.label}</h3>
                      {row.mandatory ? (
                        <Badge tone="neutral">
                          <Lock className="size-2.5" />
                          Always on
                        </Badge>
                      ) : implied ? (
                        <Badge tone="info">
                          <Info className="size-2.5" />
                          Required by another service
                        </Badge>
                      ) : row.enabled ? (
                        <Badge tone="success">
                          <Check className="size-2.5" />
                          Enabled
                        </Badge>
                      ) : (
                        <Badge tone="neutral">
                          <ToggleLeft className="size-2.5" />
                          Off
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs leading-relaxed text-muted">{row.description}</p>
                  </div>

                  <Can
                    permission="core.service.manage"
                    // Without the manage permission the state is still worth
                    // seeing — read-only rather than hidden, so somebody can
                    // tell whether a screen is missing because of the switch.
                    fallback={
                      <Badge tone={row.enabled ? 'success' : 'neutral'}>
                        {row.enabled ? 'On' : 'Off'}
                      </Badge>
                    }
                  >
                    <Switch
                      checked={row.enabled}
                      disabled={locked || setService.isPending}
                      onChange={(event) => {
                        const next = event.target.checked;
                        // Turning something off is the consequential direction,
                        // so it asks for a note first. Turning one on is
                        // additive and goes straight through.
                        if (!next) {
                          setNoteFor(row.id);
                          setNote('');
                          return;
                        }
                        setService.mutate({ id: row.id, enabled: true });
                      }}
                      aria-label={`${row.enabled ? 'Disable' : 'Enable'} ${row.label}`}
                    />
                  </Can>
                </div>

                {row.locked_reason ? (
                  <p className="rounded-control bg-surface-2 px-2.5 py-1.5 text-2xs leading-relaxed text-muted">
                    {row.locked_reason}
                  </p>
                ) : null}

                {row.missing_requirements.length > 0 && !row.enabled ? (
                  <p className="text-2xs text-warning">
                    Needs{' '}
                    {row.missing_requirements
                      .map((id) => rows.find((r) => r.id === id)?.label ?? id)
                      .join(', ')}{' '}
                    to be enabled first.
                  </p>
                ) : null}

                {/* What enabling it actually adds. An enrollment toggle whose
                    consequences are invisible is one nobody will touch. */}
                {row.modules.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-2xs font-medium uppercase tracking-wide text-faint">
                      Adds {row.modules.length} screen{row.modules.length === 1 ? '' : 's'}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {row.modules.map((module) => (
                        <span
                          key={module.id}
                          className="rounded-pill border border-line px-1.5 py-0.5 text-2xs text-muted"
                          title={
                            module.kinds.includes('client_user')
                              ? 'Also visible to client users in the client app'
                              : undefined
                          }
                        >
                          {module.label}
                          {module.kinds.includes('client_user') ? ' ·' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {noteOpen ? (
                  <div className="space-y-2 rounded-control border border-line bg-surface-2 p-2.5">
                    <Textarea
                      label={`Why is ${row.label} being switched off?`}
                      hint="Optional, and worth writing. In six months this is the only record of the decision."
                      rows={2}
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setNoteFor(null);
                          setNote('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        loading={setService.isPending}
                        onClick={() =>
                          setService.mutate({
                            id: row.id,
                            enabled: false,
                            note: note.trim() || undefined,
                          })
                        }
                      >
                        Switch off
                      </Button>
                    </div>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
